import * as React from 'react';
import clsx from 'clsx';
import { ChatView, type GroupChatState } from '@/features/chat/components/ChatView';
import { useDraftChat } from '../hooks/useDraftChat';
import { useAuth } from '@/hooks/useAuth';
import { DraftChatProvider } from '../context/DraftChatContext';
import type { Echo } from '@/types';
import type { EchoKey } from '@/utils/constants';
import type { EchoDraftResponse } from '@/types/echo_creation';

interface DraftTestChatCardProps {
  draft: EchoDraftResponse;
}

/**
 * Renders the test-chat UI for an in-progress echo draft.
 * Delegates to the real ChatView via its `groupChat` override so the test
 * session gets the same header, message actions, and suggestion UI as a
 * published echo — only the chat transport differs (draft test-chat stream).
 */
export const DraftTestChatCard: React.FC<DraftTestChatCardProps> = ({ draft }) => {
  const echoName = draft.name || 'My Echo';
  const { user } = useAuth();
  const isAudioEnabled = user?.audio_enabled ?? false;
  const { messages, isLoading, sendMessage, clearMessages } = useDraftChat(
    draft.id,
    draft.greetings,
    isAudioEnabled
  );

  const syntheticEcho: Echo = React.useMemo(
    () => ({
      id: draft.id,
      name: echoName,
      category: draft.category ?? '',
      domain: draft.domain ?? '',
      imageUrl: draft.profile_image_url ?? null,
      videoMessagesEnabled:
        draft.video_messages_enabled ?? Boolean(draft.idle_video_url || draft.video_avatar_url),
      idleVideoUrl: draft.idle_video_url ?? null,
      ...(draft.bio ? { bio: draft.bio } : {}),
    }),
    [
      draft.bio,
      draft.category,
      draft.domain,
      draft.id,
      draft.idle_video_url,
      draft.profile_image_url,
      draft.video_avatar_url,
      draft.video_messages_enabled,
      echoName,
    ]
  );

  const chatState: GroupChatState = React.useMemo(
    () => ({
      messages,
      isLoading,
      error: null,
      sendMessage,
      sendAudioMessage: async () => {
        // Draft test-chat is text-only; audio input is not supported.
      },
      clearConversation: async () => {
        clearMessages();
      },
      hasMore: false,
      loadMore: () => {
        // Draft test-chat has no paginated history.
      },
      isSending: isLoading,
      echoInfoMap: {},
    }),
    [messages, isLoading, sendMessage, clearMessages]
  );

  return (
    <div
      className={clsx(
        'flex flex-col overflow-hidden',
        'h-1000 rounded-20 border border-dark-100',
        'lg:h-full'
      )}
    >
      <DraftChatProvider draftId={draft.id}>
        <ChatView
          echo={syntheticEcho}
          echoKey={draft.id as EchoKey}
          groupChat={chatState}
          compact
        />
      </DraftChatProvider>
    </div>
  );
};
